import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './AuthContext';
import SignInModal from './SignInModal';
import HomePage from './HomePage';
import PrivacyPolicy from './PrivacyPolicy';
import DMCAPage from './DMCAPage';
import CreditsPage from './CreditsPage';
import IOSAppPage from './IOSAppPage';
import AndroidAppPage from './AndroidAppPage';
import MacOSAppPage from './MacOSAppPage';
import MacOSDirectPage from './MacOSDirectPage';
import ShellDownloadPage from './ShellDownloadPage';
import HubProductPage, { LegacyHubRedirect, isHubProductPath } from './HubProductPage';
import DocsPage from './DocsPage';
import BlogPage from './BlogPage';
import BlogPostPage from './BlogPostPage';
import PodcastPage from './PodcastPage';
import PodcastEpisodePage from './PodcastEpisodePage';
import GalleryPage from './GalleryPage';
import SeoLandingPage from './SeoLandingPage';
import CloudPage from './CloudPage';
import AccountPage from './AccountPage';
import FeedbackPage from './FeedbackPage';
import FeedbackReviewPage from './FeedbackReviewPage';
import OnboardingPage from './OnboardingPage';
// Legacy pages kept for existing URLs
import CommunityPackagePage from './CommunityPackagePage';
import MacOSAppHubPage from './MacOSAppHubPage';
import PublisherPage from './PublisherPage';
import PricingPage from './PricingPage';
import UsageDashboardPage from './UsageDashboardPage';
import DeployPage from './DeployPage';
import AdminSecurityPage from './AdminSecurityPage';
import AdminOpsPage from './AdminOpsPage';
import AdminHubPage from './AdminHubPage';
import AdminPromptsPage from './AdminPromptsPage';   // Phase PC.MOD.ADMIN
import AdminFeedbackPage from './AdminFeedbackPage'; // Phase BRANCH-Q
import WizardWisdomPage from './WizardWisdomPage';
import TemplateCandidatesAdminPage from './TemplateCandidatesAdminPage';
import AdminTMHubPage from './AdminTMHubPage';   // Phase TM-4
import AdminBlogPage from './AdminBlogPage';     // Phase Blog
import ContextMapCandidatesAdminPage from './ContextMapCandidatesAdminPage';
import ComponentCandidatesAdminPage from './ComponentCandidatesAdminPage';
import NudgesAdminPage from './NudgesAdminPage';
import TemplatesCatalogAdminPage from './TemplatesCatalogAdminPage';
import ModelCatalogAdminPage from './ModelCatalogAdminPage';
import AuthActionPage from './AuthActionPage';
import AccountMergePage from './AccountMergePage';
import HubPage from './HubPage';
import UserProfilePage from './UserProfilePage';
import FeaturesPage from './FeaturesPage';
import FeatureDetailPage from './FeatureDetailPage';
import ExamplesPage from './ExamplesPage';   // Phase PC.9
import LeaderboardPage from './LeaderboardPage';
import LeaderboardsIndexPage from './LeaderboardsIndexPage';
import DownloadsPage from './DownloadsPage';
import NotFoundPage from './NotFoundPage';
import { ensureAuth } from './authInit';
import { trackPageView } from './analytics';

// Top-level nav: kept short so it fits on a 360-wide mobile viewport without
// wrapping (user feedback 2026-05-11). Applets and Leaderboards are now
// accessible via the Products dropdown / Hub page respectively, not as
// top-level entries.
const navItems = [
  { id: 'home', label: 'mChatAI', path: '/', match: (p) => p === '/' },
  { id: 'hub', label: 'Hub', path: '/hub', match: (p) => p.startsWith('/hub') || p.startsWith('/community') || p.startsWith('/leaderboards') },
  { id: 'features', label: 'Features', path: '/features', match: (p) => p.startsWith('/features') || p.startsWith('/applets') },
  { id: 'docs', label: 'Docs', path: '/docs', match: (p) => p.startsWith('/docs') },
  { id: 'pricing', label: 'Pricing', path: '/pricing', match: (p) => p.startsWith('/pricing'), className: 'nav-wide-only' },
];

// Products dropdown — folds in Applets + Leaderboards too, so the top bar
// stays compact and these surfaces are still one click away.
const productItems = [
  { label: 'Pricing', desc: 'Plans', path: '/pricing', className: 'nav-narrow-only' },
  { label: 'Downloads', desc: 'Every app', path: '/downloads' },
  { label: 'mChatAI+', desc: 'macOS', path: '/macos-app' },
  { label: 'mChatAI+ Direct', desc: 'Direct download', path: '/macos-direct' },
  { label: 'mChatAI', desc: 'iOS', path: '/ios-app' },
  { label: 'mChatAI', desc: 'Android', path: '/android-app' },
  { label: 'mChatAIShell', desc: 'Menu bar', path: '/shell-app' },
  { label: 'mChatAI Cloud', desc: 'LLM tokens', path: '/cloud' },
  { label: 'Features', desc: 'Every applet', path: '/features' },
  { label: 'Leaderboards', desc: 'Public high scores', path: '/leaderboards' },
];

// Reddit-style avatar dropdown (2026-05-11): when signed in, the right side
// of the nav is a single circular avatar button. Tapping it opens a popover
// with "Account" + "Sign out". Frees up mobile header real estate so
// nothing has to wrap to a second row.
function AccountMenu({ user, signOut }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const firstInitial = (user.displayName || user.email || 'U')[0].toUpperCase();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%',
        }}
      >
        {user.photoURL ? (
          <img src={user.photoURL} alt="" style={{
            width: 28, height: 28, borderRadius: '50%', display: 'block',
            border: open ? '2px solid #3b82f6' : '2px solid transparent',
            transition: 'border-color 0.12s ease',
          }} />
        ) : (
          <span style={{
            width: 28, height: 28, borderRadius: '50%', background: '#3b82f6',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.75rem', color: '#fff', fontWeight: 700,
            border: open ? '2px solid #1d4ed8' : '2px solid transparent',
            transition: 'border-color 0.12s ease',
          }}>{firstInitial}</span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 200, background: '#fff',
            border: '1px solid #e5e7eb', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            padding: 6, zIndex: 1000,
          }}
        >
          <div style={{ padding: '8px 10px 10px', borderBottom: '1px solid #f3f4f6', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.displayName || 'Account'}
            </div>
            {user.email && (
              <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
            )}
          </div>
          <Link
            to="/account"
            role="menuitem"
            onClick={() => setOpen(false)}
            style={{
              display: 'block', padding: '8px 10px', fontSize: 13,
              color: '#111827', textDecoration: 'none', borderRadius: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Account
          </Link>
          <button
            role="menuitem"
            onClick={() => { setOpen(false); signOut(); navigate('/'); }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 10px', fontSize: 13,
              color: '#111827', background: 'transparent', border: 'none',
              cursor: 'pointer', borderRadius: 6, fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function TopNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const [showSignIn, setShowSignIn] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const isProductPage = pathname.startsWith('/macos') || pathname.startsWith('/ios') || pathname.startsWith('/android') || pathname.startsWith('/shell') || pathname === '/downloads';

  // Phase LOGIN-ROUTE (2026-05-17) — `/login` and `/signin` are dedicated
  // entry points for the sign-in modal so links like the game-page top-right
  // Sign-in pill ("Sign in at mchatai.com →") land directly on the modal
  // instead of showing a blank-body page with nothing but the nav. The
  // routes themselves are defined below as `element={null}` so React Router
  // doesn't 404; TopNav owns the modal lifecycle.
  const isAuthRoute = pathname === '/login' || pathname === '/signin';
  useEffect(() => {
    if (isAuthRoute && !user) setShowSignIn(true);
  }, [isAuthRoute, user]);
  // Already-signed-in users hitting /login should bounce to /account so they
  // don’t see a modal asking them to sign in again.
  useEffect(() => {
    if (isAuthRoute && user) navigate('/account', { replace: true });
  }, [isAuthRoute, user, navigate]);
  const handleCloseSignIn = () => {
    setShowSignIn(false);
    // If the user dismissed the modal that we auto-opened via /login, send
    // them somewhere sensible (home) so they don’t end up staring at the
    // empty /login surface that triggered the modal in the first place.
    if (isAuthRoute) navigate('/', { replace: true });
  };

  // A Hub product page (/hub/<slug>) draws its own one-line bar with the
  // mChatAI name, ← Hub and Sign in; a second nav row above it is the chrome
  // that page was built to remove.
  if (isHubProductPath(pathname)) return null;

  return (
    <>
      <nav className="site-nav" onMouseLeave={() => setProductsOpen(false)}>
        <div className="site-nav-inner">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`site-nav-link ${item.className || ''} ${item.match(pathname) ? 'site-nav-active' : ''}`}
            >
              {item.label}
            </Link>
          ))}

          {/* Products dropdown */}
          <div className={`nav-dropdown ${productsOpen ? 'open' : ''}`}>
            <button
              className="nav-dropdown-trigger"
              onClick={() => setProductsOpen(!productsOpen)}
              onMouseEnter={() => setProductsOpen(true)}
              style={isProductPage ? { color: '#111827', fontWeight: 600, background: '#f3f4f6' } : {}}
            >
              Products <span style={{ fontSize: '0.6rem', marginLeft: 2 }}>▼</span>
            </button>
            <div className="nav-dropdown-menu">
              {productItems.map((item) => (
                <Link key={item.path} to={item.path} className={`nav-dropdown-item ${item.className || ''}`} onClick={() => setProductsOpen(false)}>
                  {item.label}
                  <span>{item.desc}</span>
                </Link>
              ))}
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            {user ? (
              <AccountMenu user={user} signOut={signOut} />
            ) : (
              <button
                onClick={() => setShowSignIn(true)}
                className="site-nav-link"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontFamily: 'inherit', fontSize: 'inherit',
                }}
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </nav>
      {showSignIn && <SignInModal onClose={handleCloseSignIn} />}
    </>
  );
}

// Fires a GA4 page_view on every client-side route change. Without this the SPA
// only ever reported the single initial document load (gtag('config') in
// index.html), so navigation between the ~58 routes was invisible. index.html
// sets send_page_view:false so this component owns ALL pageviews (no double-count).
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function GlobalOnboardingIntercept({ children }) {
  const { user, profile, loading } = useAuthContext();
  const location = useLocation();

  if (loading) return null;

  // Intercept logged in users with empty profile, except when already on onboarding page
  if (user && !profile && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

function App() {
  useEffect(() => { ensureAuth(); }, []);

  return (
    <AuthProvider>
      <Router>
        <RouteTracker />
        <GlobalOnboardingIntercept>
          <TopNav />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<PrivacyPolicy />} />
            <Route path="/dmca" element={<DMCAPage />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/ios-app" element={<IOSAppPage />} />
            <Route path="/android-app" element={<AndroidAppPage />} />
            <Route path="/macos-app" element={<MacOSAppPage />} />
            <Route path="/macos-direct" element={<MacOSDirectPage />} />
            <Route path="/shell-app" element={<ShellDownloadPage />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/features/:slug" element={<FeatureDetailPage />} />
            {/* The old hand-maintained /applets grid drifted out of sync with the
                app registry (it listed things that were never applets). Retired in
                favour of the manifest-driven showcase; the path still resolves. */}
            <Route path="/applets" element={<Navigate to="/features" replace />} />
            <Route path="/examples" element={<ExamplesPage />} />
            <Route path="/examples/:appletId" element={<ExamplesPage />} />
            <Route path="/examples/:appletId/:slug" element={<ExamplesPage />} />
            <Route path="/docs" element={<DocsPage />} />
            {/* Phase MM-2 — marketing-machine surfaces. */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/podcast" element={<PodcastPage />} />
            <Route path="/podcast/:slug" element={<PodcastEpisodePage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            {/* Phase MM-5 - programmatic SEO landing pages. Four intent
                prefixes for crawler-friendly URLs; one component renders all
                of them from public/seo-content/{intent}/{slug}.md. */}
            <Route path="/build/:slug" element={<SeoLandingPage intent="build" />} />
            <Route path="/automate/:slug" element={<SeoLandingPage intent="automate" />} />
            <Route path="/make/:slug" element={<SeoLandingPage intent="make" />} />
            <Route path="/recipe/:slug" element={<SeoLandingPage intent="recipe" />} />
            <Route path="/cloud" element={<CloudPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/account/review-queue" element={<FeedbackReviewPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/publish" element={<PublisherPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/usage" element={<UsageDashboardPage />} />
            <Route path="/deploy" element={<DeployPage />} />
            <Route path="/admin" element={<AdminSecurityPage />} />
            <Route path="/admin/ops" element={<AdminOpsPage />} />
            <Route path="/admin/hub" element={<AdminHubPage />} />
            <Route path="/admin/prompts" element={<AdminPromptsPage />} />
            <Route path="/admin/feedback" element={<AdminFeedbackPage />} />   {/* Phase BRANCH-Q */}
            <Route path="/admin/wisdom" element={<WizardWisdomPage />} />
            <Route path="/admin/template-candidates" element={<TemplateCandidatesAdminPage />} />
            <Route path="/admin/context-map-candidates" element={<ContextMapCandidatesAdminPage />} />
            <Route path="/admin/component-candidates" element={<ComponentCandidatesAdminPage />} />
            <Route path="/admin/nudges" element={<NudgesAdminPage />} />
            <Route path="/admin/templates" element={<TemplatesCatalogAdminPage />} />
            <Route path="/admin/model-catalog" element={<ModelCatalogAdminPage />} />
            <Route path="/admin/tm" element={<AdminTMHubPage />} />   {/* Phase TM-4 */}
            <Route path="/admin/blog" element={<AdminBlogPage />} />  {/* Phase Blog */}
            <Route path="/auth/action" element={<AuthActionPage />} />
            <Route path="/account/merge" element={<AccountMergePage />} />
            {/* Phase LOGIN-ROUTE (2026-05-17) — bare routes so React Router
                doesn't 404. TopNav watches the pathname and auto-opens the
                SignInModal; closing it routes back to /. */}
            <Route path="/login" element={null} />
            <Route path="/signin" element={null} />
            {/* Phase RT.2: /hub is the single canonical Discover surface.
                Old /play URL redirects here so bookmarks stay working. */}
            <Route path="/play" element={<Navigate to="/hub" replace />} />
            <Route path="/hub/macosapps" element={<MacOSAppHubPage />} />
            <Route path="/hub" element={<HubPage />} />
            <Route path="/leaderboards" element={<LeaderboardsIndexPage />} />
            <Route path="/leaderboards/:slug" element={<LeaderboardPage />} />
            <Route path="/user/:shortUid" element={<UserProfilePage />} />
            {/* One route for /hub/<slug>, /hub/<slug>/info and the old /play, so
                opening the info sheet never reloads the running app. */}
            <Route path="/hub/:slug/:section?" element={<HubProductPage />} />
            {/* Legacy redirects — all point at the unified /hub */}
            <Route path="/community-hub" element={<Navigate to="/hub" replace />} />
            <Route path="/community-hub/:slug" element={<LegacyHubRedirect />} />
            <Route path="/community" element={<Navigate to="/hub" replace />} />
            <Route path="/community/packages/:packageId" element={<CommunityPackagePage />} />
            <Route path="/extensions" element={<Navigate to="/hub" replace />} />
            {/* Catch-all: hosting sends every unknown path here, which used to render a blank page. Keep it last. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </GlobalOnboardingIntercept>
      </Router>
    </AuthProvider>
  );
}
export default App;
